import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { TeamPeriodSelect } from '../components/team/TeamPeriodSelect';
import { PersonaAiBadge } from '../components/PersonaAiBadge';
import { isAiGeneratedPersona } from '../lib/personaSource';
import { RecordingPrivateBadge } from '../components/practice/RecordingPrivateNotice';
import type { TeamMemberStats, TeamOrgSession, TeamStatsResponse } from '../lib/teamStats';
import {
  formatGoalScore,
  formatPracticeDuration,
  formatReachRate,
  memberReachRate,
  parseTeamPeriod,
  type TeamPeriod,
} from '../lib/teamPeriod';

export function TeamMemberPage() {
  const { userId } = useParams<{ userId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const period = parseTeamPeriod(searchParams.get('period'));
  const [member, setMember] = useState<TeamMemberStats | null>(null);
  const [sessions, setSessions] = useState<TeamOrgSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setPeriod = useCallback(
    (next: TeamPeriod) => {
      setSearchParams(next === '30d' ? {} : { period: next }, { replace: true });
    },
    [setSearchParams],
  );

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setNotFound(false);
    Promise.all([
      api.get<TeamStatsResponse>(`/practice/team/stats?period=${period}`),
      api.get<{ sessions: TeamOrgSession[] }>(
        `/practice/sessions/org?userId=${encodeURIComponent(userId)}&period=${period}`,
      ),
    ])
      .then(([statsRes, sessionsRes]) => {
        if (cancelled) return;
        const found = statsRes.data.members.find((m) => m.userId === userId) ?? null;
        if (!found) {
          setNotFound(true);
          setMember(null);
          setSessions([]);
          return;
        }
        setMember(found);
        setSessions(sessionsRes.data.sessions ?? []);
      })
      .catch((err: { response?: { status?: number } }) => {
        if (cancelled) return;
        if (err.response?.status === 404) {
          setNotFound(true);
          setMember(null);
          setSessions([]);
          return;
        }
        setError('Could not load this member.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId, period]);

  const backTo = period === '30d' ? '/team' : `/team?period=${period}`;

  if (loading) {
    return <p className="text-gray-500">Loading...</p>;
  }

  if (notFound) {
    return (
      <div>
        <Link to={backTo} className="text-sm text-gray-600 hover:text-gray-900">
          Back to team
        </Link>
        <p className="mt-6 text-gray-600">Member not found in this organization.</p>
      </div>
    );
  }

  if (error || !member) {
    return (
      <div>
        <Link to={backTo} className="text-sm text-gray-600 hover:text-gray-900">
          Back to team
        </Link>
        <p className="mt-6 text-red-600">{error ?? 'Could not load this member.'}</p>
      </div>
    );
  }

  const reach = memberReachRate(member.goalReachedCount, member.sessionCount);

  return (
    <div>
      <div className="mb-4">
        <Link to={backTo} className="text-sm text-gray-600 hover:text-gray-900">
          Back to team
        </Link>
      </div>
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-gray-900">{member.name || member.email}</h1>
          <p className="text-gray-600 mt-1">
            {member.email}
            <span className="mx-2">·</span>
            <span className="text-xs px-2 py-1 bg-gray-100 rounded font-medium">{member.role}</span>
          </p>
        </div>
        <TeamPeriodSelect value={period} onChange={setPeriod} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
        <Kpi value={String(member.sessionCount)} label="Sessions" />
        <Kpi value={String(member.completedCount)} label="Completed" />
        <Kpi
          value={member.avgGoalScore == null ? '—' : formatGoalScore(member.avgGoalScore)}
          label="Avg goal score"
        />
        <Kpi value={formatReachRate(reach)} label="Goal reached" />
        <Kpi value={formatPracticeDuration(member.totalPracticeSeconds)} label="Practice time" />
      </div>

      <div className="bg-white rounded-lg border overflow-hidden">
        <div className="p-4 border-b">
          <h2 className="font-semibold text-gray-900">Sessions</h2>
        </div>
        {sessions.length === 0 ? (
          <p className="p-8 text-center text-gray-500">No practice sessions in this period.</p>
        ) : (
          <div className="divide-y">
            {sessions.map((session) => (
              <Link
                key={session.id}
                to={`/practice/${session.id}`}
                className="block p-3 sm:p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium text-gray-900 flex items-center gap-2 flex-wrap">
                      <span className="truncate">
                        {(session.personaCount ?? 1) > 1
                          ? session.personaNames?.join(', ') || session.personaName
                          : session.personaName}
                      </span>
                      {(session.personaCount ?? 1) > 1 && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                          Committee
                        </span>
                      )}
                      {isAiGeneratedPersona(session.personaSource) && <PersonaAiBadge />}
                      {session.access === 'summary' && <RecordingPrivateBadge />}
                    </div>
                    <div className="text-xs sm:text-sm text-gray-500 mt-0.5">
                      {session.pitchStyle} &middot; {session.messageCount} messages
                      {session.goalLabel && (
                        <span className="ml-1">&middot; {session.goalLabel}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {session.goalScore !== null && (
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          session.goalReached
                            ? 'bg-green-100 text-green-800'
                            : 'bg-amber-100 text-amber-800'
                        }`}
                      >
                        {session.goalScore}/10
                      </span>
                    )}
                    <span className="text-xs sm:text-sm text-gray-400">
                      {new Date(session.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Kpi({ value, label }: { value: string; label: string }) {
  return (
    <div className="bg-white p-5 rounded-lg border shadow-sm">
      <div className="text-3xl font-bold text-green-700">{value}</div>
      <div className="text-sm text-gray-500 mt-1">{label}</div>
    </div>
  );
}
